import React, { useEffect, useState } from "react";
import { Download, QrCode, MousePointerClick, Smartphone, Monitor } from "lucide-react";
import { adminAxios } from "../../context/AdminAuth";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 days" },
  { key: "all", label: "All time" },
];

const LABELS = { hero: "Hero", download: "Download section", footer: "Footer", invite: "Invitation page" };

const Metric = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-center gap-3 rounded-xl bg-[#FBFBF9] px-4 py-3">
    <Icon className="h-4 w-4 text-[#2F5E43]" />
    <span className="text-sm text-[#4A5F52]">{label}</span>
    <span className="ml-auto font-heading text-lg font-semibold text-[#111E16]" data-testid={testId}>{value}</span>
  </div>
);

const RangeTabs = ({ value, onChange }) => (
  <div className="ml-auto flex rounded-full border border-[#E8EBE9] p-1">
    {RANGES.map((r) => (
      <button
        key={r.key}
        onClick={() => onChange(r.key)}
        className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
          value === r.key ? "bg-[#2F5E43] text-white" : "text-[#4A5F52] hover:bg-[#F2F5F2]"
        }`}
        data-testid={`appstore-range-${r.key}`}
      >
        {r.label}
      </button>
    ))}
  </div>
);

const SectionTable = ({ sections }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid="appstore-sections-table">
      <thead>
        <tr className="text-left text-xs uppercase tracking-[0.12em] text-[#8AA583]">
          <th className="py-2 font-semibold">Section</th>
          <th className="py-2 text-right font-semibold">Button taps</th>
          <th className="py-2 text-right font-semibold">QR scans</th>
          <th className="py-2 text-right font-semibold">Total</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[#E8EBE9]">
        {sections.map((s) => (
          <tr key={s.section} data-testid={`appstore-row-${s.section}`}>
            <td className="py-2.5 text-[#111E16]">{LABELS[s.section] || s.section}</td>
            <td className="py-2.5 text-right text-[#4A5F52]">{s.button}</td>
            <td className="py-2.5 text-right text-[#4A5F52]">{s.section === "hero" || s.section === "download" ? s.qr : "n/a"}</td>
            <td className="py-2.5 text-right font-semibold text-[#2F5E43]" data-testid={`appstore-total-${s.section}`}>{s.total}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const AppStorePanel = () => {
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);

  useEffect(() => {
    adminAxios.get("/admin/app-store-stats", { params: { range } }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [range]);

  return (
    <div className="mt-6 rounded-2xl border border-[#2F5E43]/10 bg-white p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)]" data-testid="appstore-panel">
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#E4EDE6] text-[#2F5E43]">
          <Download className="h-4 w-4" />
        </span>
        <h2 className="font-heading text-lg font-semibold text-[#111E16]">App Store clicks</h2>
        <span className="text-sm text-[#4A5F52]">
          <span className="font-semibold text-[#2F5E43]" data-testid="appstore-total">{data?.total ?? 0}</span> total
        </span>
        <RangeTabs value={range} onChange={setRange} />
      </div>
      <div className="mt-5 grid gap-6 md:grid-cols-[1fr_1.4fr]">
        <div className="space-y-2">
          <Metric icon={MousePointerClick} label="Button taps" value={data?.button_total ?? 0} testId="appstore-button-total" />
          <Metric icon={QrCode} label="QR scans" value={data?.qr_total ?? 0} testId="appstore-qr-total" />
          <Metric icon={Smartphone} label="From phones" value={data?.devices?.phone ?? 0} testId="appstore-device-phone" />
          <Metric icon={Monitor} label="From desktops" value={data?.devices?.desktop ?? 0} testId="appstore-device-desktop" />
        </div>
        <SectionTable sections={data?.sections || []} />
      </div>
      <p className="mt-4 text-xs text-[#8AA583]">Anonymous counts only. "Today" starts at midnight UTC.</p>
    </div>
  );
};
